import type {
  BlackAndWhitePlayer,
  BlackAndWhiteState,
  CompletedRound,
  SubmitTileAction,
  TileNumber,
} from "../types";
import { compareTiles, createSubmission, getOtherPlayerId } from "../utils";

function useTile(
  player: BlackAndWhitePlayer,
  tile: TileNumber,
  scored: boolean,
): BlackAndWhitePlayer {
  return {
    ...player,
    score: scored ? player.score + 1 : player.score,
    remainingTiles: player.remainingTiles.filter((value) => value !== tile),
    usedTiles: [...player.usedTiles, tile],
  };
}

function resolveNextFirstPlayerId(
  completedRounds: readonly CompletedRound[],
): string {
  for (let index = completedRounds.length - 1; index >= 0; index -= 1) {
    const winnerId = completedRounds[index]?.winnerId;
    if (winnerId !== null && winnerId !== undefined) {
      return winnerId;
    }
  }

  const firstCompletedRound = completedRounds[0];
  if (!firstCompletedRound) {
    throw new Error("Completed round is required to advance");
  }

  return firstCompletedRound.firstPlayerId;
}

function advanceAfterCompletedRound(
  state: BlackAndWhiteState,
  completedRound: CompletedRound,
  players: readonly BlackAndWhitePlayer[],
): BlackAndWhiteState {
  const completedRounds = [...state.completedRounds, completedRound];
  const winner =
    completedRound.winnerId === null
      ? null
      : players.find((player) => player.playerId === completedRound.winnerId);
  const targetScore = state.targetScore ?? 5;
  const maxRounds = state.maxRounds ?? 9;

  if (winner && winner.score >= targetScore) {
    return {
      ...state,
      status: "COMPLETED",
      players,
      completedRounds,
      currentRound: null,
      winnerId: winner.playerId,
    };
  }

  if (completedRounds.length >= maxRounds) {
    const [first, second] = players;
    if (!first || !second) {
      throw new Error("Black and White requires exactly two players");
    }
    const winnerId =
      first.score === second.score
        ? null
        : first.score > second.score
          ? first.playerId
          : second.playerId;
    return {
      ...state,
      status: "COMPLETED",
      players,
      completedRounds,
      currentRound: null,
      winnerId,
    };
  }

  const nextFirstPlayerId = resolveNextFirstPlayerId(completedRounds);
  const playerIds = players.map((player) => player.playerId);

  return {
    ...state,
    players,
    completedRounds,
    currentRound: {
      roundNumber: completedRound.roundNumber + 1,
      phase: "FIRST_SELECTION",
      firstPlayerId: nextFirstPlayerId,
      secondPlayerId: getOtherPlayerId(playerIds, nextFirstPlayerId),
      firstSubmission: null,
    },
  };
}

export function applySubmitTile(
  state: BlackAndWhiteState,
  action: SubmitTileAction,
): BlackAndWhiteState {
  const currentRound = state.currentRound;
  if (!currentRound) return state;

  const submission = createSubmission(action.playerId, action.tile);

  if (currentRound.phase === "FIRST_SELECTION") {
    return {
      ...state,
      currentRound: {
        ...currentRound,
        phase: "SECOND_SELECTION",
        firstSubmission: submission,
      },
    };
  }

  const firstSubmission = currentRound.firstSubmission;
  if (!firstSubmission) return state;

  const outcome = compareTiles(firstSubmission.tile, action.tile);
  const winnerId =
    outcome === "DRAW"
      ? null
      : outcome === "FIRST_WIN"
        ? currentRound.firstPlayerId
        : currentRound.secondPlayerId;
  const completedRound: CompletedRound = {
    roundNumber: currentRound.roundNumber,
    firstPlayerId: currentRound.firstPlayerId,
    secondPlayerId: currentRound.secondPlayerId,
    firstSubmission,
    secondSubmission: submission,
    outcome,
    winnerId,
  };
  const players = state.players.map((player) => {
    if (player.playerId === currentRound.firstPlayerId) {
      return useTile(
        player,
        firstSubmission.tile,
        winnerId === player.playerId,
      );
    }
    if (player.playerId === currentRound.secondPlayerId) {
      return useTile(player, action.tile, winnerId === player.playerId);
    }
    return player;
  });

  return advanceAfterCompletedRound(state, completedRound, players);
}
